import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useApiClient } from "@/api/client-context";
import type { StartScopedFailureReason } from "@/api/endpoints/start-scoped-case";
import { Skeleton } from "@/components/ui/skeleton";
import type { MessageStart } from "@/domain/process-model/message-start";
import { processModelFileQueryOptions } from "@/features/process-design/use-process-model";

import { TaskForm } from "./task-form";
import { useStartScopedApplication } from "./use-start-scoped-application";

/** 失敗の理由 → 画面の案内。再試行で直るものと直らないものを分ける。 */
const REASON_KEYS = {
  no_approvers: "newApplication.scopedStart.noApprovers",
  approver_resolution_failed: "newApplication.scopedStart.resolutionFailed",
  not_configured: "newApplication.scopedStart.notConfigured",
  reserved_key: "newApplication.scopedStart.failed",
  initiator_mismatch: "newApplication.scopedStart.failed",
  unknown: "newApplication.scopedStart.failed",
} as const satisfies Record<StartScopedFailureReason, string>;

export type ScopedStartFormProps = {
  /** BPMN から読んだ起票の情報（message 名・起票フォーム・承認レーン）。 */
  readonly scoped: MessageStart;
  readonly processModelId: string;
  readonly onStarted: () => void;
};

/**
 * メッセージ起動のモデルの起票フォーム。
 *
 * 起案タスクを持たないので、フォームの定義はモデル内のファイルから読む。
 * 承認者と申請部署は組織構造から決まるので入力欄を持たない（利用者に選ばせない）。
 */
export function ScopedStartForm({
  scoped,
  processModelId,
  onStarted,
}: ScopedStartFormProps) {
  const { t } = useTranslation("common");
  const client = useApiClient();

  const schema = useQuery({
    ...processModelFileQueryOptions(
      client,
      processModelId,
      scoped.formSchemaFile ?? "",
    ),
    enabled: scoped.formSchemaFile !== null,
  });
  const uiSchema = useQuery({
    ...processModelFileQueryOptions(
      client,
      processModelId,
      scoped.formUiSchemaFile ?? "",
    ),
    enabled: scoped.formUiSchemaFile !== null,
  });

  const start = useStartScopedApplication();
  const parsed = parseJson(schema.data?.contents);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-sm">
        {t("newApplication.scopedStart.description")}
      </p>

      {schema.isPending ? <Skeleton className="h-64 w-full" /> : null}

      {schema.isError || (schema.data && parsed === null) ? (
        <p role="alert" className="text-destructive text-sm">
          {t("newApplication.noForm")}
        </p>
      ) : null}

      {start.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {t(REASON_KEYS[start.error.reason])}
        </p>
      ) : null}

      {parsed !== null ? (
        <TaskForm
          schema={parsed}
          uiSchema={parseJson(uiSchema.data?.contents) ?? {}}
          formData={{}}
          isSubmitting={start.isPending}
          onSubmit={(businessData) =>
            start.mutate({ scoped, businessData }, { onSuccess: onStarted })
          }
        />
      ) : null}
    </div>
  );
}

/** 壊れた JSON でも画面を落とさない（設計中のファイルが不正なこともある）。 */
function parseJson(
  contents: string | undefined,
): Record<string, unknown> | null {
  if (contents === undefined || contents === "") return null;
  try {
    const value = JSON.parse(contents) as unknown;
    return typeof value === "object" && value !== null
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}
